import { i18n } from '@lt/localization';
import { timeBySpeedGen } from '../../../utils/dateUtils';
import { roundToDecimals } from '../../../utils/numberUtils';
import { NBSP } from '../../../constants/common';
import { HOTEL_FACTS_TEXTS, HOTEL_FACTS_TYPES } from '../store/hotel';

const getDistanceText = (distance: number) => {
  const useMeters = distance < 1000;
  const value = useMeters ? distance : roundToDecimals(distance, 1);
  const unit = i18n.t('hotel.facts.distance', {
    context: useMeters ? 'm' : 'km',
  });

  return `${value}${NBSP}${unit}`;
};

const isBeachlineValidData = (
  data: unknown,
): data is { distance?: number | null; line: number | null } => {
  if (
    data === null ||
    typeof data !== 'object' ||
    !('line' in data) ||
    !('distance' in data) ||
    !(typeof data.line === 'number' || data.line === null) ||
    !(
      typeof data.distance === 'number' ||
      data.distance === null ||
      data.distance === undefined
    )
  ) {
    return false;
  }

  return true;
};

const getFactsItemData = (
  type: (typeof HOTEL_FACTS_TYPES)[keyof typeof HOTEL_FACTS_TYPES],
  data: Record<string, string | number | boolean | object | undefined>[string],
) => {
  if (!data || data === 'NONE') {
    return null;
  }

  let title = '';
  let subtitle = '';
  let iconText = '';
  let iconName = '';

  switch (type) {
    case HOTEL_FACTS_TYPES.AIRPORT:
      if (typeof data !== 'number') return null;

      title = i18n.t('hotel.facts.airport.title', {
        context: data > 10000 ? 'far' : 'nearby',
      });
      subtitle = `${i18n.t('hotel.facts.airport.subtitle')} ${timeBySpeedGen(
        data,
      )}`;
      iconName = 'plane';
      break;
    case HOTEL_FACTS_TYPES.BEACHLINE: {
      if (!isBeachlineValidData(data)) {
        return null;
      }

      const { distance, line } = data;

      if (!line) {
        return null;
      }

      title = HOTEL_FACTS_TEXTS[type][line].title;
      subtitle = distance
        ? `${i18n.t('hotel.facts.beachline.subtitle')} ${i18n.t(
            'hotel.facts.beachline.title',
            { context: line === 3 ? 'about' : 'all' },
          )} ${getDistanceText(distance)}`
        : i18n.t('hotel.facts.beachline.noData');
      iconText = `${line}${i18n.t('hotel.facts.beachline.line')}`;
      iconName = HOTEL_FACTS_TEXTS[type][line].iconName;
      break;
    }
    case HOTEL_FACTS_TYPES.SKI_LIFT:
      title = HOTEL_FACTS_TEXTS[type].title;
      iconName = HOTEL_FACTS_TEXTS[type].iconName;
      subtitle = `${i18n.t('hotel.facts.skiLift.in')} ${getDistanceText(
        data as number,
      )}`;
      break;
    // Факты с уровнем вложенности
    case HOTEL_FACTS_TYPES.WIFI:
    case HOTEL_FACTS_TYPES.BEACHSURFACE:
    case HOTEL_FACTS_TYPES.SKI_IN_SKI_OUT:
    case HOTEL_FACTS_TYPES.BEACHTYPE: {
      const nestedElementType = HOTEL_FACTS_TEXTS[type];
      const element = nestedElementType[String(data).toUpperCase()];

      title = element.title;
      subtitle = element.subtitle;
      iconName = element.iconName;
      break;
    }
    // Остальные факты без вложенности
    default: {
      const elementType = HOTEL_FACTS_TEXTS[type];

      title = elementType.title;
      subtitle = elementType.subtitle;
      iconName = elementType.iconName;
      break;
    }
  }

  return {
    title,
    subtitle,
    iconText,
    iconName,
  };
};

export default getFactsItemData;
